import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  Divider,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  EventSeat,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  Business,
  Verified,
  CalendarToday,
  Repeat,
  Schedule,
  LockOutlined,
} from '@mui/icons-material';
import {
  formatHours,
  formatOperationalDays,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  formatFullAddress,
  getAmenityIcon,
  PlanFeature,
  Reveal,
  MotionList,
  MotionItem,
  AnimatedNumber,
} from './themeHelpers';

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function ProfessionalTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const defaultDescription = `${library.name} provides a professional environment designed for productive work and focused study. Our facility offers modern amenities and a conducive atmosphere for academic excellence.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);
  const fullAddress = formatFullAddress(library);

  // Available seats display
  const availSeats = library.available_seats ?? library.capacity;
  const seatDisplay =
    availSeats !== null && availSeats !== undefined && library.capacity
      ? `${availSeats} / ${library.capacity}`
      : library.capacity
      ? String(library.capacity)
      : null;
  const seatSubLabel =
    library.available_seats !== null && library.available_seats !== undefined
      ? 'Seats Free'
      : 'Seats';

  const showLockers = library.locker_count > 0;

  // AnimatedNumber candidates for stats bar
  const availSeatsNum =
    typeof availSeats === 'number' && Number.isFinite(availSeats) ? availSeats : null;
  const capacityNum =
    typeof library.capacity === 'number' && Number.isFinite(library.capacity)
      ? library.capacity
      : null;
  const amenitiesNum = library.amenities?.length ?? 0;
  const plansNum = plans.length ?? 0;
  const lockerNum =
    showLockers && typeof library.locker_count === 'number' ? library.locker_count : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8F9FA' }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business sx={{ color: accentColor }} />
            <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
              {library.name}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => onBookNow()}
            sx={{
              bgcolor: accentColor,
              transition: 'filter 0.2s, transform 0.2s',
              '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)', transform: 'translateY(-1px)' },
            }}
          >
            Book Now
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ position: 'relative', height: { xs: '50vh', md: '65vh' }, overflow: 'hidden' }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%)',
          }}
        />

        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)' }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)' }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        {/* Hero text */}
        <Container maxWidth="lg" sx={{ position: 'absolute', bottom: 60, left: 0, right: 0 }}>
          <Reveal y={20} duration={0.65}>
            <Box sx={{ maxWidth: 600 }}>
              <Chip
                icon={<Verified sx={{ color: 'white !important' }} />}
                label="Verified Library"
                size="small"
                sx={{ bgcolor: accentColor, color: 'white', mb: 2 }}
              />
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
                {library.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white', opacity: 0.9 }}>
                <LocationOn fontSize="small" />
                <Typography>{library.address}</Typography>
              </Box>
            </Box>
          </Reveal>
        </Container>
      </Box>

      {/* Stats Bar — animated count-ups */}
      <Box sx={{ bgcolor: 'white', py: 3, borderBottom: '1px solid #e0e0e0' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {/* Seats */}
            {seatDisplay && (
              <Grid item xs={3} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: accentColor }}>
                    {availSeatsNum !== null ? (
                      <AnimatedNumber value={availSeatsNum} duration={1.3} />
                    ) : capacityNum !== null ? (
                      <AnimatedNumber value={capacityNum} duration={1.3} />
                    ) : (
                      seatDisplay
                    )}
                    {(availSeatsNum !== null || capacityNum !== null) && library.capacity && availSeats !== library.capacity
                      ? ` / ${library.capacity}`
                      : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{seatSubLabel}</Typography>
                </Box>
              </Grid>
            )}

            {/* Amenities */}
            <Grid item xs={3} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: accentColor }}>
                  <AnimatedNumber value={amenitiesNum} duration={1.1} suffix="+" />
                </Typography>
                <Typography variant="body2" color="text.secondary">Amenities</Typography>
              </Box>
            </Grid>

            {/* Plans */}
            <Grid item xs={3} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: accentColor }}>
                  <AnimatedNumber value={plansNum} duration={0.9} />
                </Typography>
                <Typography variant="body2" color="text.secondary">Plans</Typography>
              </Box>
            </Grid>

            {/* Lockers (4th stat when present) */}
            {showLockers && (
              <Grid item xs={3} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: accentColor }}>
                    {lockerNum !== null ? (
                      <AnimatedNumber value={lockerNum} duration={1.2} />
                    ) : (
                      library.locker_count
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Lockers</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Left Column - About & Amenities & Gallery */}
          <Grid item xs={12} md={8}>
            {/* About Section */}
            <Reveal y={16} duration={0.5}>
              <Card elevation={0} sx={{ mb: 4, border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ color: accentColor }} />
                    About the Library
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {library.description || defaultDescription}
                  </Typography>
                </CardContent>
              </Card>
            </Reveal>

            {/* Amenities Section */}
            {library.amenities?.length > 0 && (
              <Reveal delay={0.08} y={16} duration={0.5}>
                <Card elevation={0} sx={{ mb: 4, border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                      Facilities &amp; Amenities
                    </Typography>
                    <MotionList
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                        gap: 12,
                      }}
                    >
                      {library.amenities.map((amenity, idx) => (
                        <MotionItem key={idx}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              p: 2,
                              bgcolor: '#f8f9fa',
                              borderRadius: 1,
                              transition: 'background-color 0.22s, border-color 0.22s',
                              border: '1px solid transparent',
                              '&:hover': {
                                bgcolor: `${accentColor}0a`,
                                borderColor: `${accentColor}30`,
                              },
                            }}
                          >
                            <Box sx={{ color: accentColor }}>
                              {React.cloneElement(getAmenityIcon(amenity), { fontSize: 'small' })}
                            </Box>
                            <Typography variant="body2">{amenity}</Typography>
                          </Box>
                        </MotionItem>
                      ))}
                    </MotionList>
                  </CardContent>
                </Card>
              </Reveal>
            )}

            {/* Gallery Section */}
            <Reveal delay={0.16} y={16} duration={0.5}>
              <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                    Gallery
                  </Typography>
                  <MotionList
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {images.slice(0, 6).map((img, idx) => (
                      <MotionItem key={idx}>
                        <Box
                          component="img"
                          src={img}
                          alt={`Gallery ${idx + 1}`}
                          onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                          loading="lazy"
                          decoding="async"
                          sx={{
                            width: '100%',
                            height: 150,
                            objectFit: 'cover',
                            borderRadius: 1,
                            display: 'block',
                            cursor: 'pointer',
                            transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                            '&:hover': {
                              transform: 'scale(1.04)',
                              boxShadow: '0 6px 22px rgba(0,0,0,0.14)',
                            },
                          }}
                        />
                      </MotionItem>
                    ))}
                  </MotionList>
                </CardContent>
              </Card>
            </Reveal>
          </Grid>

          {/* Right Column - Plans (sticky) & Contact */}
          <Grid item xs={12} md={4}>
            {/* Plans Card */}
            <Card elevation={0} sx={{ mb: 4, border: '1px solid #e0e0e0', position: 'sticky', top: 20 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  Membership Plans
                </Typography>

                {plans.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {plans.map((plan) => {
                      const planName = plan.name || titleCasePlanType(plan.plan_type);
                      const price = formatPrice(plan.price_cents);
                      const period = planPeriodLabel(plan.plan_type);
                      const discPct = plan.first_time_discount;
                      const discPrice = discountedFirstPrice(plan.price_cents, discPct);

                      return (
                        <Box
                          key={plan.id}
                          sx={{
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            transition: 'border-color 0.22s ease, box-shadow 0.22s ease',
                            '&:hover': {
                              borderColor: accentColor,
                              boxShadow: `0 4px 16px ${accentColor}18`,
                            },
                          }}
                        >
                          {/* Plan name + discount chip */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {planName}
                            </Typography>
                            {discPct > 0 && (
                              <Chip
                                label={`${discPct}% OFF`}
                                size="small"
                                sx={{
                                  bgcolor: `${accentColor}18`,
                                  color: accentColor,
                                  fontWeight: 700,
                                  fontSize: '0.6rem',
                                  height: 20,
                                }}
                              />
                            )}
                          </Box>

                          {/* Price */}
                          {price && (
                            <Box sx={{ mb: 1 }}>
                              {discPct > 0 && discPrice !== null ? (
                                <>
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{ textDecoration: 'line-through', display: 'block' }}
                                  >
                                    {price}
                                  </Typography>
                                  <Typography variant="h5" sx={{ fontWeight: 700, color: accentColor, lineHeight: 1.2 }}>
                                    {formatPrice(discPrice)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    first time, then {price}{period ? ` ${period}` : ''}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Typography variant="h5" sx={{ fontWeight: 700, color: accentColor }}>
                                    {price}
                                  </Typography>
                                  {period && (
                                    <Typography variant="caption" color="text.secondary">{period}</Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          )}

                          {/* Feature rows */}
                          {plan.duration_days && (
                            <PlanFeature icon={<CalendarToday />} color="text.secondary">
                              Valid {plan.duration_days} days
                            </PlanFeature>
                          )}
                          <PlanFeature icon={<Schedule />} color="text.secondary">
                            {hoursLabel(plan.hours_per_day)}
                          </PlanFeature>
                          {plan.recurring && (
                            <PlanFeature icon={<Repeat />} color="text.secondary">
                              Auto-renews
                            </PlanFeature>
                          )}

                          {/* Description */}
                          {plan.description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mt: 0.75,
                              }}
                            >
                              {plan.description}
                            </Typography>
                          ) : null}

                          {/* Per-plan select — deep-links to the booking prompt for this plan */}
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            onClick={() => onBookNow(plan)}
                            sx={{
                              mt: 1.5,
                              borderColor: accentColor,
                              color: accentColor,
                              transition: 'all 0.2s',
                              '&:hover': { bgcolor: accentColor, color: 'white', borderColor: accentColor },
                            }}
                          >
                            Select Plan
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography color="text.secondary" gutterBottom>
                      Contact us for pricing
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => onBookNow()}
                  sx={{
                    bgcolor: accentColor,
                    transition: 'filter 0.2s, transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      bgcolor: accentColor,
                      filter: 'brightness(0.9)',
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${accentColor}40`,
                    },
                  }}
                >
                  Book Now
                </Button>
              </CardContent>
            </Card>

            {/* Contact Card */}
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Address */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <LocationOn sx={{ color: accentColor, mt: 0.5 }} />
                    <Typography variant="body2">{fullAddress || library.address}</Typography>
                  </Box>

                  {/* Opening hours */}
                  {(hoursStr || daysStr) && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <AccessTime sx={{ color: accentColor, mt: 0.5 }} />
                      <Box>
                        {hoursStr && (
                          <Typography variant="body2">{hoursStr}</Typography>
                        )}
                        {daysStr && (
                          <Typography variant="caption" color="text.secondary">{daysStr}</Typography>
                        )}
                        {!hoursStr && (
                          <Typography variant="body2" color="text.secondary">Contact for hours</Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Lockers */}
                  {showLockers && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LockOutlined sx={{ color: accentColor }} />
                      <Typography variant="body2">{library.locker_count} lockers available</Typography>
                    </Box>
                  )}

                  {/* Phone */}
                  {library.contact_phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Phone sx={{ color: accentColor }} />
                      <Typography variant="body2">
                        <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_phone}
                        </a>
                      </Typography>
                    </Box>
                  )}

                  {/* Email */}
                  {library.contact_email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Email sx={{ color: accentColor }} />
                      <Typography variant="body2">
                        <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_email}
                        </a>
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Map */}
                {library.lat && library.lng ? (
                  <Box sx={{ mt: 3, height: 200, borderRadius: 1, overflow: 'hidden' }}>
                    <iframe
                      title="Location"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                    />
                  </Box>
                ) : (
                  <Box sx={{ mt: 3, height: 200, borderRadius: 1, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Map unavailable</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Seats card (extra detail below contact) */}
            {seatDisplay && (
              <Card elevation={0} sx={{ mt: 4, border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EventSeat sx={{ color: accentColor }} />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: accentColor }}>
                        {seatDisplay}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{seatSubLabel}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#1a1a1a', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business sx={{ color: 'white' }} />
              <Typography sx={{ color: 'white' }}>{library.name}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Powered by{' '}
              <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor }}>
                BookMyLibrary
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: 'black' } }}>
        <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 1 }}>
          <Close />
        </IconButton>
        <Box component="img" src={images[currentImageIndex]} alt="Gallery" loading="lazy" decoding="async" sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
      </Dialog>
    </Box>
  );
}

export default ProfessionalTheme;
